import type { ValueOf } from 'type-fest';
import type { Brand } from './types';
import { initZanalytics as initZillowAnalytics, isInitialized } from '@zillow/universal-analytics';
import { getCurrentEnvForClientSide, PRODUCTION } from '@zg-rentals/environment-utils/client';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';
import { clientOnly, getAnalyticsErrorType, getCountName, getLogPrefix } from './utils';

const API_HOSTS = {
  prod: 'e.zg-api.com',
  test: 'analytics-zg-api.test.zillow.net',
} as const;

const BRAND_SPECIFIC_FIELDS = {
  zillow: {
    apiKey: { prod: 'z_prod', test: 'z_test' },
    writeKey: { prod: 'zrm_prod_web', test: 'zrm_dev_web' },
  },
  hotpads: {
    apiKey: { prod: 'hp_prod_web', test: 'hp_dev_web' },
    writeKey: { prod: 'hp_prod_web_nl', test: 'hp_dev_web_nl' },
  },
} as const;

function createConfig(brandedFields: ValueOf<typeof BRAND_SPECIFIC_FIELDS>) {
  const isProd = getCurrentEnvForClientSide() === PRODUCTION;
  return {
    apiKey: isProd ? brandedFields.apiKey.prod : brandedFields.apiKey.test,
    apiHost: isProd ? API_HOSTS.prod : API_HOSTS.test,
    secure: true,
    writeKey: isProd ? brandedFields.writeKey.prod : brandedFields.writeKey.test,
  };
}

function getAnalyticsConfig(brand: keyof typeof BRAND_SPECIFIC_FIELDS) {
  return {
    [brand]: createConfig(BRAND_SPECIFIC_FIELDS[brand]),
  };
}

export const initZanalytics = clientOnly((brand: Brand = 'zillow') => {
  try {
    if (isInitialized()) return;

    initZillowAnalytics(window, document, getAnalyticsConfig(brand), 'zanalytics');
    // useful for monitoring/debugging as these counts should match `browser init`
    getGlobalLogger()?.debug(getLogPrefix('zanalytics initialized'), { brand });
    getGlobalMonitor()?.count({ name: getCountName('initZanalytics') });
  } catch (error) {
    logError({ error, errorType: getAnalyticsErrorType('InitZanalytics') });
  }
});
